import { useState, useEffect, useRef } from 'react';
import AlertDialog from '../../components/AlertDialog';

import { useNavigate, useParams } from 'react-router-dom';
import { pedidosService, type PedidoResponse } from '../../services/pedidos.service';
import { ordenarExamesPadrao } from '../../utils/exames.utils';
import { useToast, ToastContainer } from '../../components/Toast';
import { documentosService } from '../../services/documentos.service';
import { agendamentosService } from '../../services/agendamentos.service';
import { limparRascunhoAgendamento } from '../../utils/agendamentoStorage';
import { MessageCircle, RefreshCw, Mail, Phone } from 'lucide-react';
import { copyToClipboard } from '../../utils/clipboard';
import { maskEmail, maskTelefone } from '../../utils/formatters';
import { getCustomerApiBaseURL } from '../../utils/apiConfig';
import { formatDateBR, formatDateTimeBR } from '../../utils/dateUtils';
import PageContainer from '../../components/PageContainer';

interface Agendamento {
  id: number;
  pedido_id?: number;
  colaborador_id: number;
  exame_id: number;
  variacao_id?: number;
  variacao_exame_clinico_id?: number;
  clinica_id?: number | null;
  data_agendamento?: string | null;
  valor_variacao?: number;
  avulso?: boolean;
  escolha_clinica_tipo?: 'rh' | 'colaborador';
  token_escolha_clinica?: string;
  status_id?: number;
  status?: {
    id?: number;
    valor?: string;
    descricao?: string;
  };
  colaborador: {
    id: number;
    nome: string;
    cpf: string;
    telefone?: string;
    email?: string;
    data_admissao?: string;
    data_demissao?: string;
  };
  exame: {
    id: number;
    nome: string;
  };
  exame_clinico?: {
    id: number;
    nome: string;
    preco?: number;
  };
  variacao?: {
    id: number;
    nome: string;
    variacao_exame_clinico?: Array<{
      id: number;
      preco?: number;
      exame_clinico: {
        id: number;
        nome: string;
      };
    }>;
  };
  clinica?: {
    id: number;
    nome: string;
    endereco?: string;
    numero?: string;
    bairro?: string;
    cidade?: string;
    uf?: string;
  } | null;
}

export default function FinalPage() {
  const navigate = useNavigate();
  const params = useParams();

  // Alert State
  const [alertState, setAlertState] = useState<{ isOpen: boolean; title: string; message: string; variant: 'success' | 'error' | 'warning' | 'info' }>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info'
  });

  const showAlert = (title: string, message: string, variant: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setAlertState({ isOpen: true, title, message, variant });
  };

  interface PixData {
    payment_data?: {
      qr_code?: string;
      qr_code_text?: string;
      pix_cnpj?: string;
    };
    qrCodeBase64?: string;
    qrCode?: string;
    qrcode?: string;
    pixCopiaECola?: string;
    qr_code?: string;
  }

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<PedidoResponse | null>(null);
  const [pixData, setPixData] = useState<PixData | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);


  const [downloading, setDownloading] = useState<Record<string, boolean>>({});
  const [sendingWhatsapp, setSendingWhatsapp] = useState<string | null>(null);
  const { toasts, success, error: toastError, removeToast } = useToast();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const codigo = params?.codigo as string;

  useEffect(() => {
    if (codigo) {
      loadOrder();
    } else {
      setLoading(false);
    }

    // Limpar completamente qualquer rascunho de agendamento anterior ao chegar na tela final
    limparRascunhoAgendamento();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigo]);

  // Polling automático para PIX pendente
  useEffect(() => {
    if (!order) return;

    const isPixPendente = order.tipo_pagamento === 'pix' && order.status?.valor === 'pendente';

    if (isPixPendente) {
      // Iniciar polling a cada 12 segundos
      intervalRef.current = setInterval(async () => {
        setCheckingStatus(true);
        try {
          const updatedOrder = await pedidosService.getPedidoByCodigo(codigo);

          // Se status mudou de pendente para qualquer outro, recarregar
          if (updatedOrder.status?.valor !== 'pendente') {
            setOrder(updatedOrder);
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          } else {
            // Atualizar dados mesmo que status continue pendente (caso haja outras mudanças)
            setOrder(updatedOrder);
          }
        } catch (error) {
          console.error('Erro ao verificar status do pedido:', error);
        } finally {
          setCheckingStatus(false);
        }
      }, 12000); // 12 segundos
    } else {
      // Limpar interval se não for PIX pendente
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [order, codigo]);

  // Verificação de assinatura substituída pelo hook useActiveSubscription

  const loadOrder = async () => {
    try {
      if (!codigo) {
        setLoading(false);
        return;
      }

      const orderData = await pedidosService.getPedidoByCodigo(codigo);
      console.log("📦 [Frontend] Dados do pedido carregados:", {
        id: orderData.id,
        codigo_pedido: orderData.codigo_pedido,
        valor_total: orderData.valor_total,
        tipo_pagamento: orderData.tipo_pagamento,
        status: orderData.status,
        agendamentos_count: orderData.agendamentos?.length || 0,
      });
      console.log(`📊 [Frontend] Total de agendamentos recebidos: ${orderData.agendamentos?.length || 0}`);

      // Log detalhado de cada agendamento
      if (orderData.agendamentos) {
        orderData.agendamentos.forEach((ag, idx: number) => {
          const exameNome = ag.exame?.nome || 'N/A';
          const exameClinicoNome = ag.exame_clinico?.nome || 'N/A';
          const variacaoExameClinicoCount = ag.variacao?.variacao_exame_clinico?.length || 0;
          console.log(`  📋 [Frontend] Agendamento ${idx + 1}/${orderData.agendamentos?.length || 0}:`, {
            id: ag.id,
            colaborador_id: ag.colaborador_id,
            exame_id: ag.exame_id,
            exame_nome: exameNome,
            variacao_id: ag.variacao_id || 'null',
            variacao_nome: ag.variacao?.nome || 'null',
            variacao_exame_clinico_id: ag.variacao_exame_clinico_id || 'null',
            exame_clinico_id: ag.exame_clinico?.id || 'null',
            exame_clinico_nome: exameClinicoNome,
            variacao_exame_clinico_count: variacaoExameClinicoCount,
            valor_variacao: ag.valor_variacao || 0,
            avulso: ag.avulso,
            data_agendamento: ag.data_agendamento,
          });
        });
      }

      setOrder(orderData);

      const storedPixData = localStorage.getItem('pixData');
      if (storedPixData) {
        try {
          setPixData(JSON.parse(storedPixData));
        } catch (e) {
          console.warn('Erro ao parsear pixData do localStorage:', e);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar pedido:", error);
      // Tentar 1 retry automático após 1.5s caso seja latência de banco após criação de lote grande
      try {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        const retryOrder = await pedidosService.getPedidoByCodigo(codigo);
        setOrder(retryOrder);
      } catch (retryErr) {
        console.error("Retry ao carregar pedido também falhou:", retryErr);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setCheckingStatus(true);
    try {
      const updatedOrder = await pedidosService.getPedidoByCodigo(codigo);
      setOrder(updatedOrder);
    } catch (error) {
      console.error('Erro ao atualizar pedido:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleEnviarWhatsapp = async (
    colaborador: { id: number; nome: string; telefone?: string },
    tokenColaborador: string,
    agendamentosColaborador: Agendamento[]
  ) => {
    console.log('📲 [handleEnviarWhatsapp] Disparado:', {
      colaborador,
      telefone: colaborador?.telefone,
      tokenColaborador,
      agendamentosCount: agendamentosColaborador?.length
    });

    const linkAutoagendamento = `${window.location.origin}/escolha-clinica/${tokenColaborador}`;
    const fallbackText = `Olá, ${colaborador.nome}! Segue o link da SST Exames para você escolher a clínica credenciada e o melhor dia/horário para a realização dos seus exames ocupacionais: ${linkAutoagendamento}`;
    const whatsappWebUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(fallbackText)}`;

    // Se não tem telefone cadastrado no colaborador, avisa e abre WhatsApp Web como fallback
    if (!colaborador?.telefone) {
      console.warn('⚠️ [handleEnviarWhatsapp] Colaborador sem telefone cadastrado, abrindo WhatsApp Web:', colaborador);
      window.open(whatsappWebUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    const colabKey = String(colaborador.id);
    setSendingWhatsapp(colabKey);

    try {
      const tipoExame = getTipoExame(agendamentosColaborador);
      const nomeEmpresa = order?.empresa?.razao_social || order?.empresa?.nome_fantasia || 'SST Exames';

      const res = await agendamentosService.enviarWhatsappAgendamento({
        token_escolha_clinica: tokenColaborador,
        id_funcionario: colaborador.id,
        nome_funcionario: colaborador.nome,
        telefone_funcionario: colaborador.telefone,
        link_agendamento: linkAutoagendamento,
        tipo_exame: tipoExame,
        nome_empresa: nomeEmpresa,
      });

      if (res?.success) {
        success('WhatsApp oficial enviado ao colaborador com sucesso!');
      } else {
        toastError(res?.message || 'Erro ao enviar WhatsApp oficial. Abrindo WhatsApp Web...');
        window.open(whatsappWebUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err: unknown) {
      console.error('Erro ao enviar WhatsApp via AiDesk:', err);
      toastError('Não foi possível disparar pelo WhatsApp oficial. Abrindo WhatsApp Web...');
      window.open(whatsappWebUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setSendingWhatsapp(null);
    }
  };

  const formatCPF = (cpf: string) => {
    if (!cpf) return '';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatCNPJ = (cnpj: string) => {
    if (!cnpj) return '';
    const cleaned = cnpj.replace(/\D/g, '');
    if (cleaned.length !== 14) return cnpj;
    return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  const formatDate = (date: string | Date | null | undefined) => {
    if (!date) return 'Não informada';
    try {
      return formatDateBR(date);
    } catch {
      return String(date);
    }
  };

  const formatDateTime = (date: string | Date | null | undefined) => {
    if (!date) return 'Não informada';
    try {
      return formatDateTimeBR(date);
    } catch {
      return String(date);
    }
  };

  // Verificar se um exame é um tipo de exame ocupacional (Admissional, Demissional, etc)
  const isTipoExameOcupacional = (nomeExame: string): boolean => {
    const nomeLower = nomeExame.toLowerCase();
    return (
      nomeLower.includes('admissional') ||
      nomeLower.includes('demissional') ||
      nomeLower.includes('periódico') ||
      nomeLower.includes('periodico') ||
      nomeLower.includes('retorno') ||
      nomeLower.includes('mudança') ||
      nomeLower.includes('mudanca')
    );
  };

  // Retorna diretamente o nome do tipo de exame (Admissional, Demissional, etc.)
  const getTipoExame = (agendamentos: Agendamento[]) => {
    const ag = agendamentos.find((a) => a.exame?.nome) || agendamentos[0];
    const nome = ag?.exame?.nome || 'Admissional';
    return nome.replace(/^aso\s+/i, '').trim();
  };

  // Verificar se um agendamento é obrigatório
  // Obrigatório = avulso = false E variacao_id != null
  const isObrigatorio = (agendamento: Agendamento): boolean => {
    return !agendamento.avulso && agendamento.variacao_id != null;
  };

  const handleNewOrder = () => {
    // Limpar todos os dados de rascunho do localStorage
    limparRascunhoAgendamento();
    console.log('✅ [Final] localStorage limpo - iniciando novo agendamento');

    navigate('/agendamento/cadastro?tab=novo');
  };

  const handleAcompanhar = () => {
    limparRascunhoAgendamento();
    navigate('/agendamento/cadastro?tab=historico');
  };

  const handleHome = () => {
    limparRascunhoAgendamento();
    navigate('/');
  };

  const copiarCodigoPix = async () => {
    const data = (pixData?.payment_data || pixData) as Record<string, string | undefined> | null;
    const code = data?.qr_code_text || data?.qrcode || data?.qr_code || data?.pixCopiaECola;

    if (code) {
      const ok = await copyToClipboard(code);
      if (ok) {
        showAlert('Sucesso', 'Código PIX copiado!', 'success');
      } else {
        showAlert('Erro', 'Não foi possível copiar o código PIX.', 'error');
      }
    }
  };

  const handleDownloadDocument = async (tipo: 'guia' | 'aso' | 'ficha-medica' | 'kit-exame', pedidoId: number, colaboradorId: number) => {
    const key = `${tipo}_${colaboradorId}`;
    if (downloading[key]) return;

    try {
      setDownloading(prev => ({ ...prev, [key]: true }));

      // Codificar em base64 o pedidoId_colaboradorId
      const codigo = btoa(`${pedidoId}_${colaboradorId}`);

      const documentosMap = {
        'guia': { fn: documentosService.downloadGuia, name: 'Guia' },
        'aso': { fn: documentosService.downloadASO, name: 'ASO' },
        'ficha-medica': { fn: documentosService.downloadFichaMedica, name: 'Ficha' },
        'kit-exame': { fn: documentosService.downloadKitExame, name: 'KitExame' }
      };

      const docConfig = documentosMap[tipo];

      const isMobile = typeof navigator !== 'undefined' &&
        /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(navigator.userAgent);

      if (isMobile) {
        const directUrl = `${getCustomerApiBaseURL()}/documentos/${tipo}/${codigo}?view=true`;
        const opened = window.open(directUrl, '_blank');
        if (!opened) {
          window.location.href = directUrl;
        }
        return;
      }

      const blob: Blob = await docConfig.fn.call(documentosService, codigo);
      const filename = `${docConfig.name}-${pedidoId}-${colaboradorId}.pdf`;

      const url = window.URL.createObjectURL(blob);

      // Para kit-exame, abrir direto na aba
      if (tipo === 'kit-exame') {
        window.open(url, '_blank');
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

      // Cleanup de URL feito pelo browser ao fechar aba ou garbage collect, 
      // mas podemos revogar se for download. Para open, precisamos manter vivo um pouco?
      // window.open é síncrono no disparo, mas carregamento é assíncrono. Blob URL vida útil é ligada ao documento.
      if (tipo !== 'kit-exame') {
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      }

    } catch (err) {
      console.error('Erro ao baixar documento:', err);
      toastError('Erro ao gerar documento. Tente novamente.');
    } finally {
      setDownloading(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </PageContainer>
    );
  }

  if (!order) {
    return (
      <PageContainer>
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Pedido não encontrado</h1>
          <p className="text-gray-600 mb-8">Não foi possível carregar os detalhes do pedido.</p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setLoading(true);
                loadOrder();
              }}
              className="bg-emerald-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-emerald-700 transition-colors shadow-sm"
            >
              Tentar Novamente
            </button>
            <button
              onClick={handleNewOrder}
              className="bg-gray-100 text-gray-700 px-6 py-2.5 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
            >
              Voltar ao Início
            </button>
          </div>
        </div>
      </PageContainer>
    );
  }

  const statusValor = order.status?.valor || 'pendente';
  const isPendente = statusValor === 'pendente';
  const isPix = order.tipo_pagamento === 'pix';
  const isPixPendente = isPix && isPendente;

  // Agrupar agendamentos por colaborador E data de agendamento
  console.log(`🔄 [Frontend] Agrupando ${order.agendamentos?.length || 0} agendamentos por colaborador e data...`);

  const agendamentosPorColaboradorEData = order.agendamentos?.reduce((acc, agendamento) => {
    const colaboradorId = agendamento.colaborador_id;
    // Normalizar data para YYYY-MM-DD para agrupamento
    const dataNormalizada = agendamento.data_agendamento
      ? String(agendamento.data_agendamento).split('T')[0]
      : 'sem-data';

    const key = `${colaboradorId}_${dataNormalizada}`;

    if (!acc[key]) {
      acc[key] = {
        colaborador: agendamento.colaborador,
        data_agendamento: dataNormalizada,
        agendamentos: [],
        variacao: agendamento.variacao,
        status: agendamento.status
      };
    }
    acc[key].agendamentos.push(agendamento);
    return acc;
  }, {} as Record<string, {
    colaborador: Agendamento['colaborador'],
    data_agendamento: string,
    agendamentos: Agendamento[],
    variacao?: Agendamento['variacao'],
    status?: Agendamento['status']
  }>) || {};

  console.log(`✅ [Frontend] Agrupamento concluído: ${Object.keys(agendamentosPorColaboradorEData).length} grupos criados`);

  // Agrupar por colaborador para exibição (agrupar grupos de mesma data)
  const gruposPorColaborador = Object.values(agendamentosPorColaboradorEData).reduce((acc, grupo) => {
    const colaboradorId = grupo.colaborador.id;
    if (!acc[colaboradorId]) {
      acc[colaboradorId] = {
        colaborador: grupo.colaborador,
        gruposPorData: []
      };
    }
    acc[colaboradorId].gruposPorData.push(grupo);
    return acc;
  }, {} as Record<number, {
    colaborador: Agendamento['colaborador'],
    gruposPorData: Array<{
      data_agendamento: string,
      agendamentos: Agendamento[],
      variacao?: Agendamento['variacao'],
      status?: Agendamento['status']
    }>
  }>);

  return (
    <PageContainer>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      <AlertDialog
        isOpen={alertState.isOpen}
        onClose={() => setAlertState(prev => ({ ...prev, isOpen: false }))}
        title={alertState.title}
        message={alertState.message}
        variant={alertState.variant}
      />

      <div className="w-full">
        {/* Barra Superior - Protocolo e Ações Rápidas */}
        <div className="bg-white rounded-2xl shadow-xs border border-gray-200/80 p-5 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-lg shadow-2xs">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                    Agendamento Confirmado
                  </span>
                  <span className="text-xs text-gray-400">•</span>
                  <span className="text-xs text-gray-500">
                    Emitido em {formatDateTime(order.data_criacao)}
                  </span>
                </div>
                <h1 className="text-xl md:text-2xl font-extrabold text-gray-900 mt-0.5">
                  Protocolo #{order.codigo_pedido || order.id}
                </h1>
              </div>
            </div>

            {/* Ações de navegação rápida no topo */}
            <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
              <button
                type="button"
                onClick={handleAcompanhar}
                className="flex-1 md:flex-initial inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs md:text-sm font-semibold shadow-xs transition-all hover:shadow"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
                Acompanhar Agendamentos
              </button>
              <button
                type="button"
                onClick={handleNewOrder}
                className="flex-1 md:flex-initial inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-white hover:bg-gray-50 text-gray-700 text-xs md:text-sm font-semibold border border-gray-300 shadow-2xs transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Novo Agendamento
              </button>
              <button
                type="button"
                onClick={handleHome}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-100 text-xs md:text-sm font-medium transition-colors"
                title="Ir para o Início"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <span className="hidden sm:inline">Home</span>
              </button>
            </div>
          </div>
        </div>

        {/* PIX - QR Code no topo (apenas se pendente e PIX) */}
        {isPixPendente && (pixData || order.tipo_pagamento === 'pix') && (
          <div className="bg-white rounded-2xl shadow-xs border border-gray-200/80 p-6 mb-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {checkingStatus && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    )}
                    <p className="text-sm text-blue-800">
                      <strong>Atualização automática:</strong> Esta página atualiza automaticamente assim que o pagamento for confirmado.
                    </p>
                  </div>
                  <button
                    onClick={handleRefresh}
                    disabled={checkingStatus}
                    className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {checkingStatus ? 'Atualizando...' : 'Atualizar Agora'}
                  </button>
                </div>
              </div>

              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-6">
                  Pagamento do Agendamento via PIX
                </h3>

                {(() => {
                  const qrCodeSrc = pixData?.payment_data?.qr_code || pixData?.qrCodeBase64 || pixData?.qr_code;
                  if (!qrCodeSrc) return null;
                  return (
                    <div className="bg-white p-6 rounded-lg mb-6 inline-block shadow-xs border border-emerald-100">
                      <img
                        src={
                          qrCodeSrc.startsWith('data:image')
                            ? qrCodeSrc
                            : `data:image/png;base64,${qrCodeSrc}`
                        }
                        alt="QR Code PIX"
                        className="mx-auto max-w-xs"
                      />
                    </div>
                  );
                })()}

                {(pixData?.payment_data?.qr_code_text || pixData?.qrCode || pixData?.qrcode || pixData?.pixCopiaECola) && (
                  <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-4 mb-6 max-w-lg mx-auto">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-gray-700">
                        Código PIX (Copiar e Colar):
                      </p>
                      <button
                        type="button"
                        onClick={copiarCodigoPix}
                        className="bg-emerald-600 text-white px-3 py-1.5 rounded text-xs hover:bg-emerald-700 transition-colors font-semibold"
                      >
                        Copiar Código
                      </button>
                    </div>
                    <code className="text-xs font-mono break-all block bg-gray-50 p-2.5 rounded border text-gray-900 text-left">
                      {pixData?.payment_data?.qr_code_text || pixData?.qrCode || pixData?.qrcode || pixData?.pixCopiaECola}
                    </code>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Hero Card Corporativo: Agendamento Realizado com Sucesso! */}
        <div className="bg-white rounded-2xl shadow-xs border border-gray-200/80 p-6 md:p-8 mb-6 overflow-hidden">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 md:gap-8">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 mb-3.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                Operação Concluída com Sucesso
              </div>

              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight mb-2.5">
                Agendamento Realizado com Sucesso!
              </h2>

              <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-5">
                Os exames ocupacionais foram registrados e direcionados para atendimento. Todas as etapas de comunicação e liberação de guias foram disparadas automaticamente.
              </p>

              {/* Banner de Disparos Automáticos Efetuados */}
              <div className="bg-emerald-50/70 border border-emerald-200/90 rounded-xl p-4 shadow-2xs">
                <div className="flex items-center gap-2 text-emerald-950 font-bold text-xs md:text-sm mb-3">
                  <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  <span>Comunicações e Disparos Automáticos Efetuados:</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs">
                  <div className="flex items-start gap-2.5 bg-white/90 p-3 rounded-lg border border-emerald-100 shadow-2xs">
                    <span className="text-lg leading-none mt-0.5">💬</span>
                    <div>
                      <span className="font-bold text-gray-900 block">Colaborador Notificado</span>
                      <p className="text-gray-600 mt-0.5 leading-snug">
                        Recebeu o link e orientações completas via <strong>WhatsApp</strong> e <strong>E-mail</strong>.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2.5 bg-white/90 p-3 rounded-lg border border-emerald-100 shadow-2xs">
                    <span className="text-lg leading-none mt-0.5">🏥</span>
                    <div>
                      <span className="font-bold text-gray-900 block">Clínica Credenciada</span>
                      <p className="text-gray-600 mt-0.5 leading-snug">
                        Notificada com a listagem de exames autorizados para recepcionar o colaborador.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Ilustração Aprovada */}
            <div className="flex-shrink-0 flex items-center justify-center">
              <img
                src="/image/final.svg"
                alt="Agendamento Confirmado"
                className="w-full max-w-[240px] md:max-w-[300px]"
              />
            </div>
          </div>
        </div>

        {/* Resumo do Atendimento e Dados da Empresa */}
        <div className="bg-white rounded-2xl shadow-xs border border-gray-200/80 p-5 md:p-6 mb-6">
          <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            Dados do Agendamento e da Empresa
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
            <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-100">
              <span className="text-xs font-medium text-gray-500 block mb-1">Empresa Solicitante</span>
              <p className="font-bold text-gray-900 truncate">
                {order.empresa?.nome_fantasia || order.empresa?.razao_social || 'Empresa'}
              </p>
              {order.empresa?.cnpj && (
                <p className="text-xs text-gray-500 mt-0.5">CNPJ: {formatCNPJ(order.empresa.cnpj)}</p>
              )}
            </div>

            <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-100">
              <span className="text-xs font-medium text-gray-500 block mb-1">Data da Solicitação</span>
              <p className="font-bold text-gray-900">
                {formatDateTime(order.data_criacao)}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">Registro confirmado no sistema</p>
            </div>

            <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-100">
              <span className="text-xs font-medium text-gray-500 block mb-1">Vidas e Exames</span>
              <p className="font-bold text-gray-900">
                {Object.keys(gruposPorColaborador).length} {Object.keys(gruposPorColaborador).length === 1 ? 'Colaborador' : 'Colaboradores'}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {order.agendamentos?.length || 0} exames ocupacionais
              </p>
            </div>

            <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-100">
              <span className="text-xs font-medium text-gray-500 block mb-1">Modalidade</span>
              <p className="font-bold text-emerald-700">
                Faturamento em Contrato
              </p>
              <p className="text-xs text-gray-500 mt-0.5">Consolidado em fatura mensal</p>
            </div>
          </div>
        </div>

        {/* Colaboradores e Guias de Encaminhamento */}
        <div className="bg-white rounded-2xl shadow-xs border border-gray-200/80 p-5 md:p-6 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-6 pb-4 border-b border-gray-100">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Colaboradores e Guias de Atendimento ({Object.keys(gruposPorColaborador).length})
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Baixe as guias de encaminhamento e kits médicos para entrega ou confira o link enviado ao colaborador.
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {Object.values(gruposPorColaborador).map((colaboradorGrupo, idx) => {
              const colaborador = colaboradorGrupo.colaborador;
              const pedidoId = colaboradorGrupo.gruposPorData[0]?.agendamentos[0]?.pedido_id || order.id;

              const allAgendamentosColab = colaboradorGrupo.gruposPorData.flatMap((g) => g.agendamentos);
              const tokenColaborador = allAgendamentosColab.find((ag) => ag.token_escolha_clinica)?.token_escolha_clinica;

              const isColaboradorChoice = allAgendamentosColab.some((ag) =>
                ag.escolha_clinica_tipo === 'colaborador' ||
                Boolean(ag.token_escolha_clinica) ||
                ag.status_id === 80 ||
                ag.status?.valor === 'pendente_escolha' ||
                ag.status?.valor === 'pendente'
              );

              const hasChosenClinic = allAgendamentosColab.some((ag) =>
                Boolean(
                  ag.clinica_id &&
                  ag.clinica_id !== 0 &&
                  ag.clinica &&
                  ag.clinica?.nome &&
                  ag.data_agendamento &&
                  ag.data_agendamento !== 'sem-data' &&
                  ag.status_id !== 80 &&
                  ag.status?.valor !== 'pendente' &&
                  ag.status?.valor !== 'pendente_escolha'
                )
              );

              const temAutoescolhaPendente = isColaboradorChoice && !hasChosenClinic;

              return (
                <div key={colaborador.id || idx} className="border border-gray-200 rounded-xl p-5 md:p-6 bg-white shadow-2xs">
                  {/* Cabeçalho do Colaborador com Ações de Impressão */}
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-4 mb-4 border-b border-gray-100">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="w-8 h-8 rounded-lg bg-gray-100 text-gray-700 flex items-center justify-center font-bold text-xs">
                          {colaborador.nome ? colaborador.nome.charAt(0).toUpperCase() : 'C'}
                        </span>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 leading-tight">
                            {colaborador.nome}
                          </h3>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-1 font-mono">
                            {colaborador.cpf && <span>CPF: {formatCPF(colaborador.cpf)}</span>}
                            {colaborador.email ? (
                              <span
                                className="font-sans text-gray-700 bg-white border border-gray-200 px-2 py-0.5 rounded text-[11px] flex items-center gap-1 shadow-2xs"
                                title={`E-mail: ${colaborador.email}`}
                              >
                                <Mail className="w-3 h-3 text-emerald-600 shrink-0" />
                                <span>{maskEmail(colaborador.email)}</span>
                              </span>
                            ) : (
                              <span className="font-sans text-gray-400 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded text-[11px] flex items-center gap-1">
                                <Mail className="w-3 h-3 text-gray-300 shrink-0" />
                                <span>Sem e-mail</span>
                              </span>
                            )}
                            {colaborador.telefone ? (
                              <span
                                className="font-sans text-gray-700 bg-white border border-gray-200 px-2 py-0.5 rounded text-[11px] flex items-center gap-1 shadow-2xs"
                                title={`Telefone: ${colaborador.telefone}`}
                              >
                                <Phone className="w-3 h-3 text-emerald-600 shrink-0" />
                                <span>{maskTelefone(colaborador.telefone)}</span>
                              </span>
                            ) : (
                              <span className="font-sans text-gray-400 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded text-[11px] flex items-center gap-1">
                                <Phone className="w-3 h-3 text-gray-300 shrink-0" />
                                <span>Sem telefone</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Botões de Download das Guias e Documentos */}
                    {temAutoescolhaPendente || !hasChosenClinic ? (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 text-amber-800 border border-amber-200 text-xs font-semibold">
                        <svg className="w-4 h-4 text-amber-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>
                          {temAutoescolhaPendente
                            ? 'Aguardando escolha da clínica pelo colaborador para liberação da Guia e Kit'
                            : 'Aguardando definição da clínica para liberação da Guia e Kit'}
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleDownloadDocument('guia', pedidoId!, colaborador.id)}
                          disabled={downloading[`guia_${colaborador.id}`]}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-2xs transition-colors disabled:opacity-50"
                          title="Baixar Guia de Encaminhamento"
                        >
                          {downloading[`guia_${colaborador.id}`] ? (
                            <svg className="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          )}
                          <span>{downloading[`guia_${colaborador.id}`] ? 'Gerando...' : 'Baixar Guia'}</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDownloadDocument('kit-exame', pedidoId!, colaborador.id)}
                          disabled={downloading[`kit-exame_${colaborador.id}`]}
                          className="bg-purple-600 hover:bg-purple-700 text-white px-3.5 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-2xs transition-colors disabled:opacity-50"
                          title="Kit Exame Completo (Guia + Ficha Médica)"
                        >
                          {downloading[`kit-exame_${colaborador.id}`] ? (
                            <svg className="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                          )}
                          <span>{downloading[`kit-exame_${colaborador.id}`] ? 'Gerando...' : 'Kit Exame'}</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDownloadDocument('ficha-medica', pedidoId!, colaborador.id)}
                          disabled={downloading[`ficha-medica_${colaborador.id}`]}
                          className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50"
                          title="Imprimir Ficha Médica"
                        >
                          <span>{downloading[`ficha-medica_${colaborador.id}`] ? '...' : 'Ficha Médica'}</span>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Link para Autoescolha da Clínica pelo Colaborador (quando aplicável) */}
                  {tokenColaborador && (
                    <div className="mb-5 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-300/80 rounded-xl p-4 shadow-2xs">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-2xs font-bold bg-emerald-600 text-white">
                              Autoagendamento pelo Colaborador
                            </span>
                            <span className="text-xs text-emerald-800 font-medium">
                              (Aguardando escolha de data e local)
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 mb-2">
                            O link abaixo foi enviado para <strong>{colaborador.nome}</strong> por WhatsApp e E-mail para que ele escolha a credenciada mais conveniente. Você também pode reenviar manualmente:
                          </p>
                          <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-emerald-200">
                            <span className="text-xs font-mono text-gray-800 break-all select-all flex-1">
                              {`${window.location.origin}/escolha-clinica/${tokenColaborador}`}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-row md:flex-col sm:flex-row items-center gap-2 flex-shrink-0">
                          <button
                            type="button"
                            onClick={async () => {
                              const url = `${window.location.origin}/escolha-clinica/${tokenColaborador}`;
                              const ok = await copyToClipboard(url);
                              if (ok) {
                                success('Link de autoagendamento copiado com sucesso!');
                              } else {
                                toastError('Não foi possível copiar o link.');
                              }
                            }}
                            className="w-full sm:w-auto px-3.5 py-2 text-xs font-semibold rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors shadow-2xs text-center"
                          >
                            Copiar Link
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEnviarWhatsapp(colaborador, tokenColaborador, colaboradorGrupo.gruposPorData.flatMap(g => g.agendamentos))}
                            disabled={sendingWhatsapp === String(colaborador.id)}
                            className="w-full sm:w-auto px-3.5 py-2 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-2xs text-center flex items-center justify-center gap-1.5 disabled:opacity-60"
                          >
                            {sendingWhatsapp === String(colaborador.id) ? (
                              <>
                                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                Enviando...
                              </>
                            ) : (
                              <>
                                <MessageCircle className="w-3.5 h-3.5" />
                                Reenviar WhatsApp
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Informações dos Atendimentos Agrupados */}
                  <div className="space-y-4">
                    {colaboradorGrupo.gruposPorData.map((grupoData, dataIdx) => {
                      const getNomeExame = (ag: Agendamento): string | null => {
                        if (ag.exame_clinico?.nome) return ag.exame_clinico.nome;
                        if (ag.exame?.nome && !isTipoExameOcupacional(ag.exame.nome)) return ag.exame.nome;
                        return null;
                      };

                      const agendamentosParaExibir = grupoData.agendamentos.filter(ag => Boolean(getNomeExame(ag)));
                      const tipoExame = getTipoExame(grupoData.agendamentos);

                      return (
                        <div key={dataIdx} className="bg-gray-50/80 rounded-xl p-4 border border-gray-200/80">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <span className="text-xs font-medium text-gray-500 block mb-0.5">Tipo de Exame</span>
                              <p className="text-gray-900 font-bold text-sm">{tipoExame}</p>
                            </div>
                            <div>
                              <span className="text-xs font-medium text-gray-500 block mb-0.5">Data do Atendimento</span>
                              <p className="text-gray-900 font-bold text-sm">
                                {temAutoescolhaPendente || !hasChosenClinic || !grupoData.agendamentos[0]?.data_agendamento || grupoData.data_agendamento === 'sem-data'
                                  ? 'A definir pelo colaborador'
                                  : (grupoData.data_agendamento ? formatDate(grupoData.data_agendamento) : 'A definir')}
                              </p>
                              {colaborador.data_demissao && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {tipoExame?.toLowerCase().includes('demiss') ? 'Data de Demissão: ' : 'Data de Desligamento: '}
                                  <span className="text-gray-700 font-semibold">{formatDate(colaborador.data_demissao)}</span>
                                </p>
                              )}
                              {colaborador.data_admissao && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {tipoExame?.toLowerCase().includes('admiss') ? 'Previsão de Admissão: ' : 'Data de Admissão: '}
                                  <span className="text-gray-700 font-semibold">{formatDate(colaborador.data_admissao)}</span>
                                </p>
                              )}
                            </div>

                            {/* Local de Atendimento Único e sem duplicação */}
                            {!temAutoescolhaPendente && hasChosenClinic && grupoData.agendamentos[0]?.clinica_id && grupoData.agendamentos[0]?.clinica && (
                              <div className="md:col-span-2 bg-white p-3.5 rounded-lg border border-gray-200">
                                <span className="text-xs font-semibold text-gray-500 flex items-center gap-1.5 mb-1">
                                  <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  Local de Atendimento Credenciado
                                </span>
                                <p className="text-gray-900 font-bold text-sm">{grupoData.agendamentos[0].clinica.nome}</p>
                                <p className="text-xs text-gray-600 mt-0.5">
                                  {[
                                    grupoData.agendamentos[0].clinica.endereco,
                                    grupoData.agendamentos[0].clinica.numero
                                  ].filter(Boolean).join(', ')}
                                  {grupoData.agendamentos[0].clinica.bairro ? ` - ${grupoData.agendamentos[0].clinica.bairro}` : ''}
                                  {grupoData.agendamentos[0].clinica.cidade ? `, ${grupoData.agendamentos[0].clinica.cidade}` : ''}
                                  {grupoData.agendamentos[0].clinica.uf ? ` - ${grupoData.agendamentos[0].clinica.uf}` : ''}
                                </p>
                              </div>
                            )}

                            {grupoData.variacao && (
                              <div className="md:col-span-2">
                                <span className="text-xs font-medium text-gray-500 block mb-0.5">Atividade Complementar</span>
                                <p className="text-gray-900 text-sm">{grupoData.variacao.nome}</p>
                              </div>
                            )}
                          </div>

                          {/* Lista de Exames Ocupacionais */}
                          {agendamentosParaExibir.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                                Exames Inclusos neste Atendimento
                              </h4>
                              <div className="space-y-1.5">
                                {(() => {
                                  const examesParaOrdenar = agendamentosParaExibir.map(ag => ({
                                    id: ag.id,
                                    exame_nome: getNomeExame(ag) || '',
                                    nome: getNomeExame(ag) || '',
                                    obrigatorio: isObrigatorio(ag),
                                    agendamento: ag
                                  }));

                                  const examesOrdenados = ordenarExamesPadrao(examesParaOrdenar);

                                  return examesOrdenados.map((exameItem, agIdx) => {
                                    const ag = exameItem.agendamento as Agendamento;
                                    const nomeExame = getNomeExame(ag);
                                    const obrigatorio = isObrigatorio(ag);

                                    return (
                                      <div key={`${ag.id}-${agIdx}`} className="bg-white p-2.5 rounded-lg border border-gray-200 flex justify-between items-center text-xs">
                                        <div className="flex items-center gap-2">
                                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                          <p className="text-gray-900 font-medium">{nomeExame}</p>
                                          {obrigatorio && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-2xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                              Obrigatório
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bloco de Ações Finais de Navegação */}
        <div className="bg-white rounded-2xl shadow-xs border border-gray-200/80 p-6 md:p-8 text-center mb-10">
          <h3 className="text-lg font-bold text-gray-900 mb-1">
            Próximos Passos
          </h3>
          <p className="text-xs md:text-sm text-gray-500 mb-6 max-w-md mx-auto">
            Você pode acompanhar o status dos exames em tempo real, cadastrar um novo agendamento ou voltar ao painel principal.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-2xl mx-auto">
            <button
              type="button"
              onClick={handleAcompanhar}
              className="w-full sm:w-auto px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm shadow-xs transition-all hover:shadow hover:-translate-y-0.5 flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
              Acompanhar Agendamentos
            </button>

            <button
              type="button"
              onClick={handleNewOrder}
              className="w-full sm:w-auto px-6 py-3 rounded-xl bg-white hover:bg-gray-50 text-gray-800 font-semibold text-sm border border-gray-300 shadow-2xs transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Novo Agendamento
            </button>

            <button
              type="button"
              onClick={handleHome}
              className="w-full sm:w-auto px-6 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-sm transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Ir para a Home
            </button>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
